/* eslint-disable react/prop-types */
import React, {useEffect, useRef, useState} from 'react';
import {
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Grid,
    Stack,
} from '@mui/material';
import {useGetFormTemplatesQuery} from '../../../../api/services/formsService';
import CreateFormTemplatePreviewDialog from './CreateFormTemplatePreviewDialog';
import FormTemplatePreviewVisual from './FormTemplatePreviewVisual';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import Tooltip from '@mui/material/Tooltip';

const ClampedTemplateName = ({name}) => {
    const ref = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const checkOverflow = () => {
            if (!el) return;

            const hasOverflow =
                el.scrollHeight > el.clientHeight + 1 ||
                el.scrollWidth > el.clientWidth + 1;

            setShowTooltip(hasOverflow);
        };

        checkOverflow();

        const resizeObserver = new ResizeObserver(() => {
            checkOverflow();
        });

        resizeObserver.observe(el);

        return () => {
            resizeObserver.disconnect();
        };
    }, [name]);

    return (
        <Tooltip
            title={showTooltip ? name : ''}
            disableHoverListener={!showTooltip}
        >
            <Typography
                ref={ref}
                variant="h6"
                gutterBottom
                sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {name}
            </Typography>
        </Tooltip>
    );
};

const CreateFormTemplatePreviewList = ({
                                           onTemplateSelected,
                                           recipientAssignmentType,
                                           audience,
                                           parentAccountType,
                                           formType,
                                           templateSource,
                                       }) => {
    const {data: templates = []} = useGetFormTemplatesQuery();
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const previewEnabled = false;
    const isSchoolSource = templateSource === 'school';
    const isTriggerForm = formType === 'trigger';

    const filterBySource = (t) => {
        return isSchoolSource ? !!t.schoolId : !t.schoolId;
    };
    const filterAdmicityTemplateByFormType = (t) => {
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const filterSchoolTemplateByFormType = (t) => {
        if (!formType) return true;

        if (formType === 'shareable') {
            return t.type === 'shareable';
        }

        if (formType === 'application') {
            return t.type === 'application';
        }

        if (formType === 'email' || formType === 'selfService' || formType === 'noEmail') {
            const blockedTypes = ['application', 'shareable'];
            return !blockedTypes.includes(t.type);
        }

        return true;
    };

    const filterByRecipientAssignmentType = (t) => {
        if (formType !== 'trigger') return true;

        const rules = {
            [FORM_AUDIENCE_TYPE.employees]: [
                'Blank', 'Boarding', 'Admissions', 'Medical',
                'Notification', 'Clubs'
            ],

            [`${FORM_AUDIENCE_TYPE.parentsForChildCompletion}:without`]: [
                'Blank', 'Staff', 'Trips and Activities',
                'Medical', 'Notification', 'Clubs'
            ],

            [`${FORM_AUDIENCE_TYPE.parentsForChildCompletion}:with`]: [
                'Blank', 'Staff', 'Admissions', 'Boarding'
            ]
        };

        const key =
            recipientAssignmentType === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                ? `${recipientAssignmentType}:${parentAccountType}`
                : recipientAssignmentType;

        const disallowed = rules[key] || [];

        return !disallowed.includes(t.category);
    };

    const filterAdmicityTemplateByAudience = (t) => {

        if (
            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            parentAccountType === 'with' &&
            t.category === 'Admissions' &&
            t.type === 'contact'
        ) {
            return true;
        }

        if (
            audience === FORM_AUDIENCE_TYPE.externalUsers &&
            recipientAssignmentType === FORM_ASSIGNMENT_TYPE.employees &&
            t.type === 'contact'
        ) {
            return false;
        }

        if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
            const disallowedTypes = ['transition', 'shareable', 'application', 'offer'];
            const disallowedCategories = ['Blank'];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        if (
            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            parentAccountType === 'without'
        ) {
            const disallowedTypes = ['transition', 'shareable', 'application'];
            const disallowedCategories = [
                'Blank', 'Staff', 'Trips and Activities',
                'Medical', 'Notification', 'Clubs'
            ];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        if (
            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            parentAccountType === 'with'
        ) {
            const disallowedTypes = ['transition', 'shareable', 'application', 'offer'];
            const disallowedCategories = ['Blank', 'Staff', 'Admissions', 'Boarding'];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        if (audience === FORM_AUDIENCE_TYPE.employees) {
            const disallowedTypes = ['transition', 'shareable', 'offer', 'application'];
            const disallowedCategories = [
                'Blank', 'Boarding', 'Admissions', 'Medical',
                'Notification', 'Clubs'
            ];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        if (audience === FORM_AUDIENCE_TYPE.previousSchool) {
            const disallowedTypes = ['shareable', 'offer', 'application', 'regular'];
            const disallowedCategories = [
                'Blank', 'Medical', 'Notification', 'Clubs',
                'Trips and Activities', 'Staff'
            ];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        if (audience === FORM_AUDIENCE_TYPE.students) {
            const disallowedTypes = ['shareable', 'offer', 'application', 'transition'];
            const disallowedCategories = [
                'Blank', 'Medical', 'Notification', 'Boarding',
                'Trips and Activities', 'Admissions', 'Staff'
            ];
            return !disallowedTypes.includes(t.type) &&
                !disallowedCategories.includes(t.category);
        }

        return true;
    };

    const filterSchoolTemplateByAudience = (t) => {
        if (
            audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            parentAccountType === 'with' &&
            t.category === 'Admissions' &&
            t.type === 'contact'
        ) {
            return true;
        }
        if (
            audience === FORM_AUDIENCE_TYPE.externalUsers &&
            recipientAssignmentType === FORM_ASSIGNMENT_TYPE.employees &&
            t.type === 'contact'
        ) {
            return false;
        }
        if (
            (audience === FORM_AUDIENCE_TYPE.employees || audience === FORM_AUDIENCE_TYPE.students) &&
            t.type === 'contact'
        ) {
            return false;
        }
        if (audience === FORM_AUDIENCE_TYPE.previousSchool) {
            return t.type === 'transition';
        }

        return t.type !== 'transition';
    };

    const filteredTemplates = templates
        .filter(filterBySource)
        .filter(t => {
            if (isTriggerForm) return true;

            const passesFormType = isSchoolSource
                ? filterSchoolTemplateByFormType(t)
                : filterAdmicityTemplateByFormType(t);

            if (!passesFormType) return false;

            if (!filterByRecipientAssignmentType(t)) return false;

            return isSchoolSource
                ? filterSchoolTemplateByAudience(t)
                : filterAdmicityTemplateByAudience(t);
        });

    const emptyMessage = isSchoolSource
        ? 'No school templates exist that match this form type or audience yet.'
        : 'No Admicity templates available.';

    const handleSelectTemplate = (template) => {
        onTemplateSelected?.(template);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            p: 2,
            bgcolor: '#fff'
        }}>
            <Stack direction="row" alignItems="center" justifyContent="center" mb={5}>
                <Typography variant="h4">
                    {isSchoolSource ? 'School Templates' : 'Admicity Templates'}
                </Typography>
            </Stack>

            {filteredTemplates.length === 0
                ? (
                    <Typography color="text.secondary" textAlign="center" mt={6}>
                        {emptyMessage}
                    </Typography>
                )
                : (
                    <Box sx={{
                        maxWidth: 1500,
                        mx: 'auto',
                        width: '100%'
                    }}>
                        <Grid container spacing={3}>
                            {filteredTemplates.map((template) => (
                                <Grid key={template.id} item xs={12} sm={6} md={4} lg={3}>
                                    <Card
                                        sx={{
                                            borderRadius: 2,
                                            ovzerflow: 'hidden',
                                            border: '1px solid #e0e0e0',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
                                            },
                                        }}
                                    >
                                        <CardActionArea
                                            sx={{
                                                flexGrow: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'stretch',
                                            }}
                                            onClick={() => {
                                                if (previewEnabled) {
                                                    setPreviewTemplate(template);
                                                } else {
                                                    handleSelectTemplate(template);
                                                }
                                            }}
                                        >
                                            <FormTemplatePreviewVisual category={template.category}/>
                                            <CardContent
                                                sx={{
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                }}
                                            >
                                                <ClampedTemplateName name={template.name}/>
                                            </CardContent>
                                        </CardActionArea>

                                        <Box p={2} pt={0} display="flex" gap={1}>
                                            {previewEnabled && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setPreviewTemplate(template)}
                                                    sx={{flex: 1}}
                                                >
                                                    Preview
                                                </Button>
                                            )}
                                            <Button
                                                variant="contained"
                                                onClick={() => handleSelectTemplate(template)}
                                                sx={{
                                                    backgroundColor: '#186BBE',
                                                    '&:hover': {
                                                        backgroundColor: '#155FA7',
                                                        boxShadow: 'none'
                                                    },
                                                    boxShadow: 'none',
                                                    flex: previewEnabled ? 1 : '0 0 50%',
                                                    ml: previewEnabled ? 0 : 'auto',
                                                }}
                                            >
                                                Use
                                            </Button>
                                        </Box>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}

            {previewEnabled && (
                <CreateFormTemplatePreviewDialog
                    open={!!previewTemplate}
                    template={previewTemplate}
                    onClose={() => setPreviewTemplate(null)}
                    onUseTemplate={(t) => {
                        handleSelectTemplate(t);
                        setPreviewTemplate(null);
                    }}
                />
            )}
        </Box>
    );
};

export default CreateFormTemplatePreviewList;
